/**
 * Validaciones de la pareja XML + PDF que el usuario debe cargar juntos.
 *
 * Resultado lleva una bandera `ok` (todas pasaron) + mensajes específicos por
 * regla para que la UI los muestre como ayudas inline · cada error apunta a
 * UNO de los dos archivos para que el usuario sepa qué cambiar.
 */

export interface ResultadoValidacion {
  ok: boolean;
  errorXml: string | null;
  errorPdf: string | null;
  errorPareja: string | null;
}

const VACIO: ResultadoValidacion = {
  ok: false,
  errorXml: null,
  errorPdf: null,
  errorPareja: null,
};

const TEXT_DECODER = new TextDecoder("utf-8", { fatal: false });

/**
 * Valida que ambos archivos vengan, el XML sea CFDI 4.0 parseable, el PDF
 * tenga header válido `%PDF-` y que el nombre base coincida (case-insensitive,
 * ignorando la extensión).
 */
export async function validarParejaCfdi(
  xml: File | null,
  pdf: File | null,
): Promise<ResultadoValidacion> {
  if (!xml && !pdf) return VACIO;

  const out: ResultadoValidacion = {
    ok: false,
    errorXml: null,
    errorPdf: null,
    errorPareja: null,
  };

  // Extensión XML
  if (xml) {
    if (!xml.name.toLowerCase().endsWith(".xml")) {
      out.errorXml = "El archivo debe tener extensión .xml";
    } else if (xml.size === 0) {
      out.errorXml = "El archivo XML está vacío";
    } else {
      const validacion = await validarContenidoCfdi(xml);
      if (validacion) out.errorXml = validacion;
    }
  }

  // Extensión PDF + header
  if (pdf) {
    if (!pdf.name.toLowerCase().endsWith(".pdf")) {
      out.errorPdf = "El archivo debe tener extensión .pdf";
    } else if (pdf.size === 0) {
      out.errorPdf = "El archivo PDF está vacío";
    } else {
      const validacion = await validarHeaderPdf(pdf);
      if (validacion) out.errorPdf = validacion;
    }
  }

  // Pareja (solo si ambos vienen)
  if (xml && pdf) {
    const baseXml = baseNombre(xml.name);
    const basePdf = baseNombre(pdf.name);
    if (baseXml.toLowerCase() !== basePdf.toLowerCase()) {
      out.errorPareja =
        `El nombre del PDF "${basePdf}" debe coincidir con el del XML "${baseXml}"`;
    }
  }

  out.ok =
    !!xml &&
    !!pdf &&
    !out.errorXml &&
    !out.errorPdf &&
    !out.errorPareja;
  return out;
}

function baseNombre(nombre: string): string {
  const dot = nombre.lastIndexOf(".");
  return dot >= 0 ? nombre.substring(0, dot) : nombre;
}

/**
 * Lee los primeros 4 bytes del PDF · estándar PDF empieza con `%PDF-`.
 */
async function validarHeaderPdf(pdf: File): Promise<string | null> {
  try {
    const head = await pdf.slice(0, 5).arrayBuffer();
    const text = new TextDecoder("ascii").decode(head);
    if (!text.startsWith("%PDF-")) {
      return "El archivo no parece un PDF válido (header inválido)";
    }
    return null;
  } catch {
    return "No se pudo leer el archivo PDF";
  }
}

/**
 * Valida que el XML parsee y contenga el elemento raíz `cfdi:Comprobante`.
 * Lee solo los primeros 2KB para no procesar archivos enormes en cliente.
 */
async function validarContenidoCfdi(xml: File): Promise<string | null> {
  try {
    const head = await xml.slice(0, 4096).arrayBuffer();
    const text = TEXT_DECODER.decode(head);

    // Heurística rápida · debe contener `cfdi:Comprobante` y `Version="4.0"`.
    if (!/(cfdi:Comprobante|<\s*Comprobante)/i.test(text)) {
      return "El XML no parece un CFDI (no contiene <cfdi:Comprobante>)";
    }
    if (!/Version\s*=\s*"4\.0"/.test(text)) {
      return "Solo se aceptan CFDIs versión 4.0";
    }
    return null;
  } catch {
    return "No se pudo leer el archivo XML";
  }
}
